import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Switch } from '@/components/atoms/Switch';
import { Input } from '@/components/atoms/Form/Input';
import { computeText } from '@/locales/utils';
import {
    Container,
    SectionTitle,
    Row,
    AmountInputContainer,
    WarningMessage,
    Description,
} from './EwalletOverdraftSection.style';

interface EwalletOverdraftSectionProps {
    useEwalletOverdraft: boolean;
    ewalletOverdraftAmount: number;
    postPaymentCustomersCount: number;
    canDisableOverdraft: boolean;
    onToggleOverdraft: (value: boolean) => void;
    onAmountChange: (value: number) => void;
}

export const EwalletOverdraftSection: React.FunctionComponent<EwalletOverdraftSectionProps> = ({
    useEwalletOverdraft,
    ewalletOverdraftAmount,
    postPaymentCustomersCount,
    canDisableOverdraft,
    onToggleOverdraft,
    onAmountChange,
}) => {
    const intl = useIntl();

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value) || 0;
        // Convert from display currency to cents
        const amountInCents = Math.round(value * 100);
        onAmountChange(amountInCents);
    };

    // Convert from cents to display currency
    const displayAmount = ewalletOverdraftAmount / 100;

    const isToggleDisabled = useEwalletOverdraft && !canDisableOverdraft;

    return (
        <Container>
            <SectionTitle>
                <FormattedMessage id="brandInfo.ewalletOverdraft.title" />
            </SectionTitle>
            <Description>
                <FormattedMessage id="brandInfo.ewalletOverdraft.description" />
            </Description>

            <Row>
                <Switch
                    labelId="brandInfo.ewalletOverdraft.enable"
                    value={useEwalletOverdraft}
                    onChange={onToggleOverdraft}
                    disabled={isToggleDisabled}
                />
            </Row>

            {isToggleDisabled && (
                <WarningMessage>
                    <FormattedMessage
                        id="brandInfo.ewalletOverdraft.cannotDisable"
                        values={{ count: postPaymentCustomersCount }}
                    />
                </WarningMessage>
            )}

            {useEwalletOverdraft && (
                <AmountInputContainer>
                    <Input
                        type="number"
                        labelId="brandInfo.ewalletOverdraft.amount"
                        value={displayAmount}
                        onChange={handleAmountChange}
                        min="0.01"
                        step="0.01"
                        placeholder={computeText(intl, 'brandInfo.ewalletOverdraft.amountPlaceholder')}
                        unitType="currency"
                    />
                </AmountInputContainer>
            )}
        </Container>
    );
};
